import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import api from "../../services/api";
import { showError } from "../../services/alert";
import { geolocationErrorMessage, getCurrentDeviceLocation } from "../../utils/geolocation";

const toNumber = (value) => value === null || value === undefined || value === "" ? null : Number(value);

const formatHa = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

const TALHAO_COLORS = ["#16a34a", "#2563eb", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2", "#ea580c"];

const temCoordenada = (item) => item?.latitude && item?.longitude;

const montarBounds = (pontos) => {
  const coordenadas = pontos
    .filter((ponto) => ponto.latitude !== null && ponto.longitude !== null)
    .map((ponto) => ({ lat: Number(ponto.latitude), lon: Number(ponto.longitude) }));

  if (!coordenadas.length) {
    return null;
  }

  const minLat = Math.min(...coordenadas.map((ponto) => ponto.lat));
  const maxLat = Math.max(...coordenadas.map((ponto) => ponto.lat));
  const minLon = Math.min(...coordenadas.map((ponto) => ponto.lon));
  const maxLon = Math.max(...coordenadas.map((ponto) => ponto.lon));
  const latSpan = Math.max(maxLat - minLat, 0.018);
  const lonSpan = Math.max(maxLon - minLon, 0.018);
  const paddingLat = Math.max(latSpan * 0.65, 0.012);
  const paddingLon = Math.max(lonSpan * 0.65, 0.012);

  return {
    minLat: minLat - paddingLat,
    maxLat: maxLat + paddingLat,
    minLon: minLon - paddingLon,
    maxLon: maxLon + paddingLon
  };
};

const MAP_HEIGHT = 480;
const TILE_SIZE = 256;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const latLonToWorld = (latitude, longitude, zoom) => {
  const scale = TILE_SIZE * 2 ** zoom;
  const latRad = Number(latitude) * Math.PI / 180;
  return {
    x: (Number(longitude) + 180) / 360 * scale,
    y: (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * scale
  };
};

const worldToLatLon = (x, y, zoom) => {
  const scale = TILE_SIZE * 2 ** zoom;
  const longitude = x / scale * 360 - 180;
  const n = Math.PI - 2 * Math.PI * y / scale;
  const latitude = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { latitude, longitude };
};

const calcularCentro = (pontos) => {
  const validos = pontos.filter((ponto) => ponto.latitude !== null && ponto.longitude !== null);
  if (!validos.length) return null;

  const pontoFocal =
    validos.find((ponto) => ponto.selecionado) ||
    validos.find((ponto) => ponto.tipo === "fazenda") ||
    validos[0];

  return {
    latitude: Number(pontoFocal.latitude),
    longitude: Number(pontoFocal.longitude)
  };
};

const coordenadaPareceBrasil = (ponto) => {
  const lat = Number(ponto.latitude);
  const lon = Number(ponto.longitude);
  return lat >= -34 && lat <= 6 && lon >= -74 && lon <= -34;
};

const calcularZoomInicial = (bounds, width = 900, height = MAP_HEIGHT) => {
  if (!bounds) return 14;

  const latSpan = Math.max(Math.abs(bounds.maxLat - bounds.minLat), 0.001);
  const lonSpan = Math.max(Math.abs(bounds.maxLon - bounds.minLon), 0.001);
  const zoomLon = Math.log2((width * 360) / (TILE_SIZE * lonSpan));
  const zoomLat = Math.log2((height * 170) / (TILE_SIZE * latSpan));

  return clamp(Math.floor(Math.min(zoomLon, zoomLat)), 12, 17);
};

function MapaFazenda() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [propriedades, setPropriedades] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [propriedadeId, setPropriedadeId] = useState(params.get("propriedadeId") || "");
  const [talhaoId, setTalhaoId] = useState(params.get("talhaoId") || "");
  const [loading, setLoading] = useState(false);
  const [locatingDevice, setLocatingDevice] = useState(false);
  const [deviceLocation, setDeviceLocation] = useState(null);

  const propriedade = useMemo(
    () => propriedades.find((item) => String(item.id) === String(propriedadeId)) || null,
    [propriedades, propriedadeId]
  );

  const pontosMapa = useMemo(() => {
    const pontos = [];

    if (temCoordenada(propriedade)) {
      pontos.push({
        id: `fazenda-${propriedade.id}`,
        tipo: "fazenda",
        nome: propriedade.nome,
        areaHa: propriedade.areaHa,
        latitude: toNumber(propriedade.latitude),
        longitude: toNumber(propriedade.longitude),
        color: "#14532d"
      });
    }

    talhoes
      .filter(temCoordenada)
      .forEach((talhao, index) => {
        pontos.push({
          id: `talhao-${talhao.id}`,
          tipo: "talhao",
          nome: talhao.nome,
          areaHa: talhao.areaHa,
          latitude: toNumber(talhao.latitude),
          longitude: toNumber(talhao.longitude),
          color: TALHAO_COLORS[index % TALHAO_COLORS.length],
          selecionado: String(talhao.id) === String(talhaoId)
        });
      });

    return pontos;
  }, [propriedade, talhoes, talhaoId]);

  const mapaBounds = useMemo(() => montarBounds(pontosMapa), [pontosMapa]);
  const centroInicial = useMemo(() => calcularCentro(pontosMapa), [pontosMapa]);
  const pontosForaDoBrasil = useMemo(
    () => pontosMapa.filter((ponto) => !coordenadaPareceBrasil(ponto)),
    [pontosMapa]
  );
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(14);
  const [mapSize, setMapSize] = useState({ width: 900, height: MAP_HEIGHT });
  const mapRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const element = mapRef.current;
    if (!element) return;

    const updateSize = () => {
      setMapSize({
        width: Math.max(320, element.clientWidth || 900),
        height: MAP_HEIGHT
      });
    };

    updateSize();

    if (!window.ResizeObserver) {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!centroInicial) {
      setMapCenter(null);
      return;
    }

    setMapCenter(centroInicial);
    setMapZoom(calcularZoomInicial(mapaBounds, mapSize.width, mapSize.height));
  }, [centroInicial, mapaBounds, mapSize.width, mapSize.height]);

  const mapaProjetado = useMemo(() => {
    if (!mapCenter) return null;

    const centerWorld = latLonToWorld(mapCenter.latitude, mapCenter.longitude, mapZoom);
    const startX = centerWorld.x - mapSize.width / 2;
    const startY = centerWorld.y - mapSize.height / 2;
    const endX = centerWorld.x + mapSize.width / 2;
    const endY = centerWorld.y + mapSize.height / 2;
    const tiles = [];
    const minTileX = Math.floor(startX / TILE_SIZE);
    const maxTileX = Math.floor(endX / TILE_SIZE);
    const minTileY = Math.floor(startY / TILE_SIZE);
    const maxTileY = Math.floor(endY / TILE_SIZE);
    const tileLimit = 2 ** mapZoom;

    for (let x = minTileX; x <= maxTileX; x += 1) {
      for (let y = minTileY; y <= maxTileY; y += 1) {
        if (y < 0 || y >= tileLimit) continue;

        const wrappedX = ((x % tileLimit) + tileLimit) % tileLimit;
        tiles.push({
          key: `${mapZoom}-${x}-${y}`,
          src: `https://tile.openstreetmap.org/${mapZoom}/${wrappedX}/${y}.png`,
          left: x * TILE_SIZE - startX,
          top: y * TILE_SIZE - startY
        });
      }
    }

    const markers = pontosMapa.map((ponto) => {
      const world = latLonToWorld(ponto.latitude, ponto.longitude, mapZoom);
      return {
        ...ponto,
        left: world.x - startX,
        top: world.y - startY
      };
    });

    const deviceMarker = deviceLocation ? (() => {
      const world = latLonToWorld(deviceLocation.latitude, deviceLocation.longitude, mapZoom);
      return {
        ...deviceLocation,
        left: world.x - startX,
        top: world.y - startY
      };
    })() : null;

    return { tiles, markers, deviceMarker };
  }, [deviceLocation, mapCenter, mapSize.height, mapSize.width, mapZoom, pontosMapa]);

  const iniciarArrasteMapa = (event) => {
    if (!mapCenter) return;

    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      centerWorld: latLonToWorld(mapCenter.latitude, mapCenter.longitude, mapZoom)
    };
  };

  const moverMapa = (event) => {
    if (!dragRef.current) return;

    const dx = event.clientX - dragRef.current.x;
    const dy = event.clientY - dragRef.current.y;
    const next = worldToLatLon(
      dragRef.current.centerWorld.x - dx,
      dragRef.current.centerWorld.y - dy,
      mapZoom
    );
    setMapCenter(next);
  };

  const pararArrasteMapa = () => {
    dragRef.current = null;
  };

  const aplicarZoom = (nextZoom, anchorX = mapSize.width / 2, anchorY = mapSize.height / 2) => {
    if (!mapCenter || nextZoom === mapZoom) return;

    const centerWorld = latLonToWorld(mapCenter.latitude, mapCenter.longitude, mapZoom);
    const startX = centerWorld.x - mapSize.width / 2;
    const startY = centerWorld.y - mapSize.height / 2;
    const anchorWorld = {
      x: startX + anchorX,
      y: startY + anchorY
    };
    const anchorLatLon = worldToLatLon(anchorWorld.x, anchorWorld.y, mapZoom);
    const nextAnchorWorld = latLonToWorld(anchorLatLon.latitude, anchorLatLon.longitude, nextZoom);
    const nextCenterWorld = {
      x: nextAnchorWorld.x - anchorX + mapSize.width / 2,
      y: nextAnchorWorld.y - anchorY + mapSize.height / 2
    };

    setMapZoom(nextZoom);
    setMapCenter(worldToLatLon(nextCenterWorld.x, nextCenterWorld.y, nextZoom));
  };

  const aplicarZoomNoMouse = (event) => {
    if (!mapCenter) return;

    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const nextZoom = clamp(mapZoom + (event.deltaY < 0 ? 1 : -1), 3, 18);
    aplicarZoom(nextZoom, event.clientX - rect.left, event.clientY - rect.top);
  };

  const centralizarNoDispositivo = async () => {
    setLocatingDevice(true);
    try {
      const current = await getCurrentDeviceLocation();
      setDeviceLocation(current);
      setMapCenter({ latitude: current.latitude, longitude: current.longitude });
      setMapZoom(17);
    } catch (error) {
      showError(error?.code ? geolocationErrorMessage(error) : error?.message || geolocationErrorMessage(error));
    } finally {
      setLocatingDevice(false);
    }
  };

  const areaTalhoes = useMemo(
    () => talhoes.reduce((total, item) => total + Number(item.areaHa || 0), 0),
    [talhoes]
  );

  const percentualOcupado = propriedade?.areaHa
    ? Math.min(100, (areaTalhoes / Number(propriedade.areaHa)) * 100)
    : 0;

  const maiorArea = useMemo(
    () => Math.max(...talhoes.map((item) => Number(item.areaHa || 0)), 1),
    [talhoes]
  );

  useEffect(() => {
    const carregarPropriedades = async () => {
      setLoading(true);
      try {
        const res = await api.get("/propriedades");
        const data = res.data || [];
        setPropriedades(data);

        if (!propriedadeId && data.length) {
          const primeiraComCoordenada = data.find((item) => item.latitude && item.longitude) || data[0];
          setPropriedadeId(String(primeiraComCoordenada.id));
        }
      } catch (error) {
        showError("Erro ao carregar fazendas.");
      } finally {
        setLoading(false);
      }
    };

    carregarPropriedades();
  }, []);

  useEffect(() => {
    if (!propriedadeId) {
      setTalhoes([]);
      return;
    }

    const carregarTalhoes = async () => {
      setLoading(true);
      try {
        const res = await api.get("/talhoes", { params: { propriedadeId } });
        const data = res.data || [];
        setTalhoes(data);

        if (talhaoId && !data.some((item) => String(item.id) === String(talhaoId))) {
          setTalhaoId("");
        }
      } catch (error) {
        showError("Erro ao carregar áreas da fazenda.");
      } finally {
        setLoading(false);
      }
    };

    carregarTalhoes();
  }, [propriedadeId, talhaoId]);

  return (
    <div className="d-flex" style={{ minHeight: "100vh" }}>
      <Sidebar />
      <div className="flex-grow-1">
        <Header />
        <div className="container-fluid py-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
              <div className="d-flex align-items-center gap-3">
                <button className="btn btn-light btn-sm d-flex align-items-center gap-1" onClick={() => navigate("/menu")}>
                  <i className="bi bi-arrow-left text-success" />
                  <span className="text-success fw-semibold">Voltar</span>
                </button>
                <div>
                  <h5 className="mb-0">Mapa da Fazenda</h5>
                  <small className="text-white-50">Visualize a fazenda, talhões e área produtiva</small>
                </div>
              </div>
              <button className="btn btn-light text-success fw-bold d-flex align-items-center gap-1" onClick={() => navigate("/talhoes")}>
                <i className="bi bi-grid-1x2" />
                <span>Talhões</span>
              </button>
            </div>

            <div className="card-body">
              <div className="row g-3 mb-4">
                <div className="col-12 col-lg-4">
                  <label className="form-label fw-semibold">Fazenda</label>
                  <select
                    className="form-select"
                    value={propriedadeId}
                    onChange={(event) => {
                      setPropriedadeId(event.target.value);
                      setTalhaoId("");
                    }}
                    disabled={loading}
                  >
                    {!propriedades.length && <option value="">Nenhuma fazenda cadastrada</option>}
                    {propriedades.map((item) => (
                      <option value={item.id} key={item.id}>{item.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-lg-3">
                  <label className="form-label fw-semibold">Área / talhão</label>
                  <select
                    className="form-select"
                    value={talhaoId}
                    onChange={(event) => setTalhaoId(event.target.value)}
                    disabled={loading || !talhoes.length}
                  >
                    <option value="">Visão geral da fazenda</option>
                    {talhoes.map((item) => (
                      <option value={item.id} key={item.id}>{item.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-6 col-lg-2 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-outline-success w-100"
                    onClick={centralizarNoDispositivo}
                    disabled={locatingDevice}
                  >
                    <i className={`bi ${locatingDevice ? "bi-arrow-repeat location-spin" : "bi-crosshair"} me-1`} />
                    {locatingDevice ? "Localizando..." : "Minha localização"}
                  </button>
                </div>
                <div className="col-12 col-sm-6 col-lg-3 d-flex align-items-end">
                  <button
                    className="btn btn-outline-success w-100"
                    onClick={() => navigate("/propriedades/novo")}
                  >
                    <i className="bi bi-geo-alt me-1" />
                    Nova fazenda
                  </button>
                </div>
              </div>

              <div className="row g-4">
                <div className="col-12 col-xl-8">
                  <div className="border rounded-3 overflow-hidden" style={{ minHeight: 480 }}>
                    {mapaProjetado ? (
                      <div
                        ref={mapRef}
                        className="position-relative overflow-hidden"
                        style={{ height: mapSize.height, cursor: dragRef.current ? "grabbing" : "grab", background: "#dbe7d3" }}
                        onMouseDown={iniciarArrasteMapa}
                        onMouseMove={moverMapa}
                        onMouseUp={pararArrasteMapa}
                        onMouseLeave={pararArrasteMapa}
                        onWheel={aplicarZoomNoMouse}
                      >
                        {mapaProjetado.tiles.map((tile) => (
                          <img
                            alt=""
                            draggable={false}
                            key={tile.key}
                            src={tile.src}
                            style={{
                              position: "absolute",
                              width: TILE_SIZE,
                              height: TILE_SIZE,
                              left: tile.left,
                              top: tile.top,
                              userSelect: "none"
                            }}
                          />
                        ))}

                        <div className="position-absolute top-0 start-0 w-100 h-100 pe-none">
                          {mapaProjetado.markers.map((ponto) => {
                            const area = Number(ponto.areaHa || 0);
                            const diametro = ponto.tipo === "fazenda"
                              ? Math.min(170, Math.max(92, Math.sqrt(area || 1) * 8))
                              : Math.min(82, Math.max(34, Math.sqrt(area || 1) * 5));

                            return (
                              <div
                                key={ponto.id}
                                className="position-absolute"
                                style={{
                                  left: ponto.left,
                                  top: ponto.top,
                                  width: diametro,
                                  height: diametro,
                                  transform: "translate(-50%, -50%)",
                                  zIndex: ponto.tipo === "fazenda" ? 2 : 3
                                }}
                              >
                                <div
                                  className="rounded-circle"
                                  style={{
                                    width: diametro,
                                    height: diametro,
                                    background: ponto.tipo === "fazenda" ? "rgba(20, 83, 45, 0.2)" : `${ponto.color}33`,
                                    border: ponto.selecionado ? `4px solid ${ponto.color}` : `2px solid ${ponto.color}`,
                                    boxShadow: "0 0 0 2px rgba(255,255,255,0.75), 0 8px 24px rgba(0,0,0,0.2)"
                                  }}
                                  title={`${ponto.nome} - ${formatHa(ponto.areaHa)} ha`}
                                />
                                <div
                                  className="position-absolute rounded-circle"
                                  style={{
                                    left: "50%",
                                    top: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: ponto.tipo === "fazenda" ? 16 : 12,
                                    height: ponto.tipo === "fazenda" ? 16 : 12,
                                    background: ponto.color,
                                    border: "2px solid #fff",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.35)"
                                  }}
                                />
                                <div
                                  className="position-absolute"
                                  style={{
                                    left: "50%",
                                    top: "50%",
                                    transform: "translate(-50%, -100%)",
                                    width: 0,
                                    height: 0,
                                    borderLeft: "7px solid transparent",
                                    borderRight: "7px solid transparent",
                                    borderTop: `18px solid ${ponto.color}`,
                                    filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.35))"
                                  }}
                                />
                                <div
                                  className="position-absolute start-50 translate-middle-x px-2 py-1 rounded-2 fw-semibold"
                                  style={{
                                    top: `calc(50% + ${diametro / 2 + 8}px)`,
                                    background: "rgba(15, 23, 42, 0.88)",
                                    color: "#fff",
                                    whiteSpace: "nowrap",
                                    fontSize: 12,
                                    boxShadow: "0 3px 10px rgba(0,0,0,0.25)"
                                  }}
                                >
                                  {ponto.tipo === "fazenda" ? "Fazenda" : ponto.nome}
                                </div>
                              </div>
                            );
                          })}
                          {mapaProjetado.deviceMarker && (
                            <div
                              className="position-absolute"
                              style={{
                                left: mapaProjetado.deviceMarker.left,
                                top: mapaProjetado.deviceMarker.top,
                                transform: "translate(-50%, -50%)",
                                zIndex: 5
                              }}
                              title={`Sua localização (precisão aproximada de ${mapaProjetado.deviceMarker.accuracy} m)`}
                            >
                              <span
                                className="d-block rounded-circle"
                                style={{
                                  width: 18,
                                  height: 18,
                                  background: "#2563eb",
                                  border: "3px solid #fff",
                                  boxShadow: "0 0 0 8px rgba(37,99,235,.2), 0 3px 10px rgba(0,0,0,.35)"
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <div className="position-absolute top-0 end-0 m-3 d-flex flex-column gap-2" onMouseDown={(event) => event.stopPropagation()}>
                          <button className="btn btn-light btn-sm shadow-sm fw-bold" type="button" onClick={() => aplicarZoom(clamp(mapZoom + 1, 3, 18))}>+</button>
                          <button className="btn btn-light btn-sm shadow-sm fw-bold" type="button" onClick={() => aplicarZoom(clamp(mapZoom - 1, 3, 18))}>-</button>
                          {centroInicial && (
                            <button className="btn btn-light btn-sm shadow-sm" type="button" onClick={() => { setMapCenter(centroInicial); setMapZoom(calcularZoomInicial(mapaBounds, mapSize.width, mapSize.height)); }}>
                              Centralizar
                            </button>
                          )}
                        </div>

                        <div className="position-absolute bottom-0 start-0 m-3 p-2 rounded-3 bg-white shadow-sm">
                          <div className="small fw-semibold mb-1">Demarcacao</div>
                          <div className="d-flex align-items-center gap-2 small">
                            <span className="rounded-circle d-inline-block" style={{ width: 10, height: 10, background: "#14532d" }} />
                            <span>Fazenda</span>
                          </div>
                          <div className="d-flex align-items-center gap-2 small">
                            <span className="rounded-circle d-inline-block" style={{ width: 10, height: 10, background: "#16a34a" }} />
                            <span>Talhões</span>
                          </div>
                        </div>
                        <div className="position-absolute bottom-0 end-0 m-3 px-2 py-1 rounded-2 bg-white shadow-sm small text-muted">
                          Arraste ou use a roda do mouse para aproximar.
                        </div>
                        {pontosForaDoBrasil.length > 0 && (
                          <div className="position-absolute top-0 start-0 m-3 px-3 py-2 rounded-3 bg-warning-subtle border border-warning text-dark shadow-sm small">
                            Confira latitude/longitude: ha ponto fora da faixa do Brasil.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center text-center p-4" style={{ minHeight: 480 }}>
                        <div>
                          <i className="bi bi-map fs-1 text-success" />
                          <h5 className="mt-3">Coordenadas pendentes</h5>
                          <p className="text-muted mb-3">
                            Cadastre latitude e longitude na fazenda ou no talhão para exibir o mapa.
                          </p>
                          <button className="btn btn-success" onClick={() => propriedade ? navigate(`/propriedades/editar/${propriedade.id}`) : navigate("/propriedades/novo")}>
                            Editar coordenadas
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-xl-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white fw-semibold">Resumo da área</div>
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        <div>
                          <span className="text-muted small d-block">Fazenda</span>
                          <strong>{propriedade?.nome || "Não selecionada"}</strong>
                        </div>
                        <div className="row g-2">
                          <div className="col-6">
                            <div className="border rounded-3 p-3">
                              <span className="text-muted small d-block">Área total</span>
                              <strong>{formatHa(propriedade?.areaHa)} ha</strong>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="border rounded-3 p-3">
                              <span className="text-muted small d-block">Talhões</span>
                              <strong>{talhoes.length}</strong>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="border rounded-3 p-3">
                              <span className="text-muted small d-block">Área mapeada</span>
                              <strong>{formatHa(areaTalhoes)} ha</strong>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="border rounded-3 p-3">
                              <span className="text-muted small d-block">Uso</span>
                              <strong>{formatHa(percentualOcupado)}%</strong>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="d-flex justify-content-between small text-muted mb-1">
                            <span>Talhões cadastrados</span>
                            <span>{formatHa(areaTalhoes)} ha</span>
                          </div>
                          <div className="progress" style={{ height: 10 }}>
                            <div className="progress-bar bg-success" style={{ width: `${percentualOcupado}%` }} />
                          </div>
                        </div>

                        <div className="d-grid gap-2">
                          {talhoes.map((item) => {
                            const largura = Math.max(10, (Number(item.areaHa || 0) / maiorArea) * 100);
                            const selecionado = String(item.id) === String(talhaoId);
                            return (
                              <button
                                type="button"
                                className={`border rounded-3 p-3 text-start bg-transparent ${selecionado ? "border-success" : ""}`}
                                key={item.id}
                                onClick={() => setTalhaoId(String(item.id))}
                              >
                                <div className="d-flex justify-content-between gap-2">
                                  <strong>{item.nome}</strong>
                                  <span className="text-muted small">{formatHa(item.areaHa)} ha</span>
                                </div>
                                <div className="progress mt-2" style={{ height: 8 }}>
                                  <div className="progress-bar bg-success" style={{ width: `${largura}%` }} />
                                </div>
                                <div className="small text-muted mt-2">
                                  {item.latitude && item.longitude ? `${item.latitude}, ${item.longitude}` : "Sem coordenadas proprias"}
                                </div>
                              </button>
                            );
                          })}
                          {!talhoes.length && (
                            <div className="border rounded-3 p-3 text-muted">
                              Nenhum talhão cadastrado para esta fazenda.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MapaFazenda;
